/* eslint-disable no-useless-rename */
import { SagaIterator } from '@redux-saga/core'
import { all, put, call, delay, takeLatest } from 'redux-saga/effects'
import jwtDecode from 'jwt-decode'
import login from 'services/login'
import { setError, setLoading, setUser, userLoginSagas } from 'ducks/user/reducer'
import { setAuth } from 'ducks/auth/reducer'
import { setAuthenticatedSession } from 'services/login/auth-session'
import type { IMfaChallengeSession } from 'services/login/mfa-session'
import { MFA_LOGIN_STORAGE_KEY } from 'services/login/mfa-session'

interface IHttpError {
  response?: {
    status?: number
    data?: {
      code?: string
      message?: string
    }
  }
  message?: string
}

type RoleType = 'special_admin' | 'super_admin' | 'platform_admin' | 'super_platform_admin' | 'user'

const MINIMUM_LOGIN_TRANSITION_MS = 1500

function* waitForLoginTransition(startedAtMs: number) {
  const remainingMs = Math.max(0, MINIMUM_LOGIN_TRANSITION_MS - (Date.now() - startedAtMs))

  if (remainingMs > 0) {
    yield delay(remainingMs)
  }
}

function* loginSaga(action: any): SagaIterator {
  let isRedirectingToMfa = false

  yield put(setLoading(true))
  const loginStartedAtMs: number = Date.now()

  try {
    const response = yield call(login, action?.payload?.email, action?.payload?.password)
    const user = response?.data

    if (user?.token) {
      yield* waitForLoginTransition(loginStartedAtMs)
      const {
        company_id: companyId,
        token,
        user_name: userName,
        has_admin_platform_management: hasAdminPlatformManagement,
        has_questionnaire_management: hasQuestionnaireManagement,
      } = user

      const decodedToken: { roleType?: RoleType } = jwtDecode(token)
      setAuthenticatedSession(token, user.csrf_token)

      yield put(
        setUser({
          email: action?.payload?.email,
          token,
          companyId,
          userName,
          hasAdminPlatformManagement,
          hasQuestionnaireManagement,
        })
      )
      yield put(
        setAuth({
          role: decodedToken.roleType || '',
          isAuth: true,
        })
      )
      if (user?.message) {
        yield put(
          setError({
            error: true,
            message: user.message,
            code: 'MFA_STAGING_BYPASS',
          })
        )
      }
      yield put(setLoading(false))
    } else if (user?.mfa_required && user?.challenge_token) {
      yield* waitForLoginTransition(loginStartedAtMs)
      const challenge: IMfaChallengeSession = {
        challengeToken: user.challenge_token,
        email: user.email || action?.payload?.email || '',
        userName: user.user_name || '',
        companyId: user.company_id || 0,
        role: user.role || '',
        canRememberDevice: Boolean(user.can_remember_device),
        lastAccessAt: user.last_login_at || user.last_access_at || user.lastLoginAt || user.lastAccessAt,
        expiresInMinutes: user.expires_in_minutes || 10,
        sentAt: new Date().toISOString(),
        resendCount: 0,
        attemptsLeft: user.attempts_left ?? 5,
      }

      sessionStorage.setItem(MFA_LOGIN_STORAGE_KEY, JSON.stringify(challenge))
      isRedirectingToMfa = true
      window.location.replace('/login/mfa')
      return
    } else {
      yield* waitForLoginTransition(loginStartedAtMs)
      const message = user?.message || 'Credentials are invalid'
      yield put(
        setError({
          error: true,
          message,
          code: String(response?.status || 500),
        })
      )
      yield put(setLoading(false))
    }
  } catch (error) {
    yield* waitForLoginTransition(loginStartedAtMs)
    const httpError = error as IHttpError
    const status = httpError.response?.status || 500
    const message = httpError.response?.data?.message || httpError.message || 'Credentials are invalid'

    yield put(
      setError({
        error: true,
        message,
        code: httpError.response ? httpError.response.data?.code || String(status) : 'NETWORK_ERROR',
      })
    )
    yield put(setLoading(false))
  } finally {
    if (!isRedirectingToMfa) {
      yield put(setLoading(false))
    }
  }
}

export function* userSagas(): SagaIterator {
  yield all([takeLatest(userLoginSagas.trigger, loginSaga)])
}
